import z from "zod";
import type { StateCreator } from "zustand";

import { ANALYTICS_PARAMS } from "@/features/analytics/models";
import type { Product } from "@/features/products/models";
import type { Category } from "@/shared/domains";
import { optionalNumericString } from "@/shared/lib";

// Deliberately opaque so it reads like any other tracking param in a shared link.
export const COUPON_CODE_PARAM = "scpid";

const urlParamsSchema = z
  .looseObject({
    [ANALYTICS_PARAMS.EF_TRANSACTION_ID]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_ORDER_ID]: optionalNumericString,
    [ANALYTICS_PARAMS.EVERFLOW_AFFILIATE_ID]: optionalNumericString,
    [ANALYTICS_PARAMS.EVERFLOW_SOURCE_ID]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_UID]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_SUB1]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_SUB2]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_SUB3]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_SUB4]: z.string(),
    [ANALYTICS_PARAMS.EVERFLOW_SUB5]: z.string(),
    [ANALYTICS_PARAMS.CUSTOMER_IO_ID]: z.string(),
    [ANALYTICS_PARAMS.UTM_CAMPAIGN]: z.string(),
    [ANALYTICS_PARAMS.UTM_SOURCE]: z.string(),
    [ANALYTICS_PARAMS.UTM_MEDIUM]: z.string(),
    [ANALYTICS_PARAMS.UTM_CONTENT]: z.string(),
    klarna: z.stringbool().catch(false),
    higher_value: z.stringbool().catch(false),
    price_variant: z.string(),
  })
  .partial();

type UrlParams = z.infer<typeof urlParamsSchema>;

export interface UrlParamsSlice {
  urlParams: UrlParams;
  // False until useStoreUrlParams has run at least once, regardless of
  // whether it found a price_variant or not - lets callers that need
  // urlParams.price_variant (e.g. the pricing screens' product query) wait
  // for a definitive answer instead of racing the very first render, where
  // urlParams is always still the pre-resolution default.
  urlParamsResolved: boolean;
  couponCode?: string;
  targetCategory?: Category["slug"];
  targetProduct?: Product["slug"];
  setUrlParams: (urlParams: URLSearchParams) => void;
  setEfTransactionId: (transactionId: string) => void;
  setPriceVariant: (priceVariant: string | undefined) => void;
  setCouponCode: (couponCode: string) => void;
  setUrlParamsResolved: () => void;
  setTargetCategory: (targetCategory: Category["slug"]) => void;
  setTargetProduct: (targetProduct: Product["slug"]) => void;
}

export const createUrlParamsSlice: StateCreator<
  UrlParamsSlice,
  [],
  [],
  UrlParamsSlice
> = (set) => ({
  urlParams: {},
  urlParamsResolved: false,
  setUrlParams: (urlParams) => {
    const parsedParams = urlParamsSchema.safeParse(
      Object.fromEntries(urlParams),
    );

    if (parsedParams.success) {
      set({ urlParams: parsedParams.data });
    }
  },
  setEfTransactionId: (transactionId) => {
    set((state) => ({
      urlParams: {
        ...state.urlParams,
        [ANALYTICS_PARAMS.EF_TRANSACTION_ID]: transactionId,
      },
    }));
  },
  setPriceVariant: (priceVariant) => {
    set((state) => ({
      urlParams: {
        ...state.urlParams,
        price_variant: priceVariant,
      },
    }));
  },
  setCouponCode: (couponCode) => {
    set({ couponCode });
  },
  setUrlParamsResolved: () => {
    set({ urlParamsResolved: true });
  },
  setTargetCategory: (targetCategory) => {
    set({ targetCategory });
  },
  setTargetProduct: (targetProduct) => {
    set({ targetProduct });
  },
});
